import { Box, IconButton, FormText, FormToggle, Tooltip } from "@husqvarna/ui-core";
import MowerCapacityBadge from "../../MowerCapacityBadge";
import MowerFeatures from "./MowerFeatures/MowerFeatures";
import MowerIcon from "../../../../../shared/components/MowerIcon/MowerIcon";
import { Product } from "../../../../models/Product";
import { useIntl } from "react-intl";
import { UnitSystem } from "../../../../models/Enums";
import { UseFormReturn } from "react-hook-form";

export type MowerDialogProps = {
  mower: Product;
  capacityPercent: number;
  mowerQuantity: number;
  unitSystem: UnitSystem
  onBackClick: () => void;
  form: UseFormReturn<{ quantity: number; areaIds: string[]; possibleToMowOnDifferentHours: boolean }>;
  changeQuantity: (delta: number) => void;
  toggleCuttingHours: (checked: boolean) => void;
  possibleToMowOnDifferentHours: boolean;
  minQuantity: number;
  maxQuantity: number;
  currentQuantity: number;
  colorHex?: string;
}

const MowerDialogHeader = ({
  mower,
  mowerQuantity,
  unitSystem,
  capacityPercent,
  onBackClick,
  form,
  changeQuantity,
  toggleCuttingHours,
  possibleToMowOnDifferentHours,
  minQuantity,
  maxQuantity,
  currentQuantity,
  colorHex
} :MowerDialogProps) => {
  const intl = useIntl();
  const handleBackClick = () => {
    onBackClick.call(this);
  };

  return (
    <Box>
      <Box
        mt={2}
        ml={2}
        mr={6}
        mb={3}
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <IconButton
          icon="back"
          onClick={() => handleBackClick()}
          aria-label={intl.formatMessage({
            id: "projects.equipment.closeDrawer",
            defaultMessage: "Close",
          })}
        />
        <MowerCapacityBadge remainingCapacity={capacityPercent} />
      </Box>
      <Box flexDirection="row" ml={6} alignItems="center" justifyContent="space-between">
        <MowerFeatures
          selectedProduct={mower}
          quantity={mowerQuantity}
          unitSystem={unitSystem}
          colorHex={colorHex}
        />
        <Box flexDirection="column" alignItems="center">
          <Box mb={1}>
            <MowerIcon product={mower} size="large" />
          </Box>
          {/* Quantity */}
          <Box mx={6} alignItems="center" justifyContent="flex-end">
            <Box flexDirection="row" gap={1} alignItems="center">
              <IconButton
                size="Medium"
                icon="minus"
                onClick={() => changeQuantity(-1)}
                disabled={currentQuantity <= minQuantity}
                aria-label="subtract quantity"
              />
              <FormText
                className="quantity-input"
                label=""
                {...form.register("quantity", { valueAsNumber: true, min: minQuantity, max: maxQuantity })}
              />
              <IconButton
                size="Medium"
                icon="plus"
                onClick={() => changeQuantity(1)}
                disabled={currentQuantity >= maxQuantity}
                aria-label="add quantity"
              />
            </Box>
          </Box>
        </Box>
      </Box>
      
      {/* Cutting Hours Toggle */}
      <Box mx={6} py={3} mt={6} mb={0} className="equipment-details-schedule">
        <FormToggle
          checked={possibleToMowOnDifferentHours}
          onChange={(e: any) => toggleCuttingHours(e.checked)}
          label={intl.formatMessage({
            id: "project.equipment.mowerSelection.canCutOnDifferentHours",
            defaultMessage: "Can mow areas on different hours",
          })}
        />
        <Tooltip
          toolTipText={intl.formatMessage({
            id: "project.equipment.mowerSelection.canCutOnDifferentHours.tooltip",
            defaultMessage: "If the chosen areas have to be mowed during the same time of the day, the machine capacity can be extensively affected.",
          })}
        >
          <IconButton icon="info" variant="Ghost" />
        </Tooltip>
      </Box>
    </Box>
  );
}

export default MowerDialogHeader;
